import { useEffect, useState } from "react";
import { LifeBuoy } from "lucide-react";
import {
  Button, Input, Label, Textarea,
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { portal } from "../../../lib/portal";

/**
 * Start a conversation with the desk: a subject and a message.
 *
 * A dialog is right here, where it was wrong for a document: this is a short
 * form that belongs to the screen behind it, not something to be read. Both
 * fields have a visible label - a placeholder that doubles as the label is gone
 * the moment the reader starts typing, which is when they need to check what
 * the box was for.
 */
export function NewTicketDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const t = useT();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubject("");
    setBody("");
    setFailed(false);
  }, [open]);

  async function submit() {
    if (!subject.trim() || !body.trim() || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const tk = await portal.createTicket(subject.trim(), body.trim());
      onCreated(tk.id);
    } catch {
      // The dialog stays open with the text in it, and says what happened.
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="size-4 text-primary" />
            {t("portal.support.new")}
          </DialogTitle>
          <DialogDescription>{t("portal.support.newLead")}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="ticket-subject">{t("portal.support.subject")}</Label>
            <Input
              id="ticket-subject"
              value={subject}
              maxLength={255}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t("portal.support.subjectPlaceholder")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ticket-body">{t("portal.support.message")}</Label>
            <Textarea
              id="ticket-body"
              value={body}
              rows={6}
              maxLength={10000}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t("portal.support.messagePlaceholder")}
            />
          </div>
          {failed && <p role="alert" className="text-sm text-destructive">{t("portal.support.sendFailed")}</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" loading={busy} disabled={!subject.trim() || !body.trim()}>
              {t("portal.support.send")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
